import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  CalendarIcon,
  Clock,
  Plus,
  Filter,
  Loader2,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Download
} from "lucide-react";
import { format } from "date-fns";

// Admin-only Excel export — date range + filters → downloads Excel
const ExportFollowupsControls = () => {
  // Replicates the Call History export UX. Visible only to admins.
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [exp, setExp] = useState({ start_date: "", end_date: "", assigned_to: "", priority: "", completed: "", lead_date_filter: "none", lead_date_start: "", lead_date_end: "" });

  useEffect(() => {
    axios.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (['admin','super_admin'].includes(user?.role)) {
      // Default: last 30 days
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      setExp((e) => ({
        ...e,
        start_date: start.toISOString().slice(0, 10),
        end_date: end.toISOString().slice(0, 10),
      }));
      axios.get(`${API}/users`).then((r) => setAgents(r.data || [])).catch(() => {});
    }
  }, [user?.role]);

  const handleDownload = async () => {
    if (exp.lead_date_filter !== "none") {
      if (!exp.lead_date_start && !exp.lead_date_end) {
        toast.error("Pick at least a Start or End date for the lead filter, or set it to 'None'");
        return;
      }
      if (exp.lead_date_start && exp.lead_date_end && exp.lead_date_start > exp.lead_date_end) {
        toast.error("Lead filter: Start date must be on or before End date");
        return;
      }
    }
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (exp.start_date) params.append("start_date", exp.start_date);
      if (exp.end_date) params.append("end_date", exp.end_date);
      if (exp.assigned_to) params.append("assigned_to", exp.assigned_to);
      if (exp.priority) params.append("priority", exp.priority);
      if (exp.completed) params.append("completed", exp.completed);
      if (exp.lead_date_filter !== "none") {
        params.append("lead_date_filter", exp.lead_date_filter);
        if (exp.lead_date_start) params.append("lead_date_start", exp.lead_date_start);
        if (exp.lead_date_end) params.append("lead_date_end", exp.lead_date_end);
      }
      const res = await axios.get(`${API}/followups/export-excel?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `followups_${exp.start_date}_to_${exp.end_date}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Follow-ups exported");
      setOpen(false);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("No follow-ups found for the selected filters");
      } else {
        toast.error(err.response?.data?.detail || "Failed to export follow-ups");
      }
    } finally {
      setDownloading(false);
    }
  };

  if (!["admin", "super_admin"].includes(user?.role)) return null;

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
        data-testid="export-followups-btn"
      >
        <Download className="w-4 h-4 mr-2" />
        Download Report
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="export-followups-dialog">
          <DialogHeader>
            <DialogTitle>Download Follow-ups Report</DialogTitle>
            <DialogDescription>Pick filters and export to Excel.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">From (Scheduled Date)</Label>
                <Input type="date" value={exp.start_date} onChange={(e) => setExp({ ...exp, start_date: e.target.value })} data-testid="fup-export-start" />
              </div>
              <div>
                <Label className="text-xs">To (Scheduled Date)</Label>
                <Input type="date" value={exp.end_date} onChange={(e) => setExp({ ...exp, end_date: e.target.value })} data-testid="fup-export-end" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Assigned Agent (optional)</Label>
              <Select value={exp.assigned_to || "_all_"} onValueChange={(v) => setExp({ ...exp, assigned_to: v === "_all_" ? "" : v })}>
                <SelectTrigger className="h-9" data-testid="fup-export-agent"><SelectValue placeholder="All agents" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all_">All agents</SelectItem>
                  {agents.filter((a) => a.is_active).map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={exp.completed || "_all_"} onValueChange={(v) => setExp({ ...exp, completed: v === "_all_" ? "" : v })}>
                  <SelectTrigger className="h-9" data-testid="fup-export-status"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">All</SelectItem>
                    <SelectItem value="false">Pending</SelectItem>
                    <SelectItem value="true">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={exp.priority || "_all_"} onValueChange={(v) => setExp({ ...exp, priority: v === "_all_" ? "" : v })}>
                  <SelectTrigger className="h-9" data-testid="fup-export-priority"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">All</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Lead-based date filter (optional) */}
            <div className="pt-2 border-t border-slate-200 space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Additional Lead-based filter (optional)</Label>
              <Select value={exp.lead_date_filter} onValueChange={(v) => setExp({ ...exp, lead_date_filter: v, ...(v === "none" ? { lead_date_start: "", lead_date_end: "" } : {}) })}>
                <SelectTrigger className="h-9" data-testid="fup-export-lead-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="created">Lead Created Date</SelectItem>
                  <SelectItem value="activity">Lead Last Activity Date</SelectItem>
                </SelectContent>
              </Select>
              {exp.lead_date_filter !== "none" && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Lead From</Label>
                    <Input type="date" value={exp.lead_date_start} onChange={(e) => setExp({ ...exp, lead_date_start: e.target.value })} data-testid="fup-export-lead-filter-start" />
                  </div>
                  <div>
                    <Label className="text-xs">Lead To</Label>
                    <Input type="date" value={exp.lead_date_end} onChange={(e) => setExp({ ...exp, lead_date_end: e.target.value })} data-testid="fup-export-lead-filter-end" />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={downloading}>Cancel</Button>
            <Button onClick={handleDownload} disabled={downloading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fup-export-confirm">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const FollowUps = () => {
  const [followups, setFollowups] = useState([]);
  const [leads, setLeads] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    completed: "",
    priority: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [allLeads, setAllLeads] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [newFollowup, setNewFollowup] = useState({
    lead_id: "",
    title: "",
    description: "",
    priority: "medium"
  });

  const fetchFollowups = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (filters.completed !== "") params.append("completed", filters.completed);
      if (filters.priority) params.append("priority", filters.priority);

      const response = await axios.get(`${API}/followups?${params.toString()}`);
      const followupsData = Array.isArray(response.data) ? response.data : [];
      setFollowups(followupsData);

      // Fetch lead names for follow-ups that have lead_ids
      const leadIds = [...new Set(followupsData.map(f => f.lead_id).filter(Boolean))];
      if (leadIds.length > 0) {
        const leadPromises = leadIds.map(id => 
          axios.get(`${API}/leads/${id}`).catch(() => null)
        );
        const leadResponses = await Promise.all(leadPromises);
        const leadsMap = {};
        leadResponses.forEach(res => {
          if (res?.data) {
            leadsMap[res.data.id] = res.data;
          }
        });
        setLeads(leadsMap);
      }
    } catch (error) {
      console.error("Failed to fetch followups:", error);
      toast.error("Failed to load follow-ups");
      setFollowups([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      // The leads endpoint returns { leads: [...], total: n, ... }
      setAllLeads(response.data.leads || []);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
      setAllLeads([]);
    }
  };

  useEffect(() => {
    fetchFollowups();
    fetchAllLeads();
  }, [filters]);

  const handleCreateFollowup = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/followups`, {
        ...newFollowup,
        scheduled_at: selectedDate.toISOString()
      });
      toast.success("Follow-up scheduled");
      setShowAddDialog(false);
      setNewFollowup({
        lead_id: "",
        title: "",
        description: "",
        priority: "medium"
      });
      fetchFollowups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create follow-up");
    }
  };

  const handleToggleComplete = async (followup) => {
    try {
      await axios.patch(`${API}/followups/${followup.id}`, { 
        completed: !followup.completed 
      });
      toast.success(followup.completed ? "Marked as pending" : "Marked as complete");
      fetchFollowups();
    } catch (error) {
      toast.error("Failed to update follow-up");
    }
  };

  const handleDelete = async (followupId) => {
    if (!window.confirm("Are you sure you want to delete this follow-up?")) return;
    
    try {
      await axios.delete(`${API}/followups/${followupId}`);
      toast.success("Follow-up deleted");
      fetchFollowups();
    } catch (error) {
      toast.error("Failed to delete follow-up");
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "bg-red-50 text-red-700 border-red-200",
      medium: "bg-yellow-50 text-yellow-700 border-yellow-200",
      low: "bg-slate-50 text-slate-600 border-slate-200"
    };
    return colors[priority] || "bg-slate-50 text-slate-600 border-slate-200";
  };

  const isOverdue = (scheduledAt) => {
    return new Date(scheduledAt) < new Date() && !scheduledAt.completed;
  };

  const groupedFollowups = {
    overdue: followups.filter(f => !f.completed && isOverdue(f.scheduled_at)),
    today: followups.filter(f => {
      const scheduled = new Date(f.scheduled_at);
      const today = new Date();
      return !f.completed && 
        scheduled.toDateString() === today.toDateString();
    }),
    upcoming: followups.filter(f => {
      const scheduled = new Date(f.scheduled_at);
      const today = new Date();
      return !f.completed && 
        scheduled > today && 
        scheduled.toDateString() !== today.toDateString();
    }),
    completed: followups.filter(f => f.completed)
  };

  const renderFollowupCard = (followup) => {
    const lead = leads[followup.lead_id];
    const overdue = !followup.completed && isOverdue(followup.scheduled_at);

    return (
      <div
        key={followup.id}
        className={`p-4 rounded-md border ${
          followup.completed ? 'bg-slate-50 border-slate-200 opacity-70' : 
          overdue ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'
        }`}
        data-testid={`followup-${followup.id}`}
      >
        <div className="flex items-start gap-3">
          <Checkbox
            checked={followup.completed}
            onCheckedChange={() => handleToggleComplete(followup)}
            className="mt-1"
            data-testid={`toggle-followup-${followup.id}`}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-medium ${followup.completed ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                {followup.title}
              </span>
              <span className={`${getPriorityColor(followup.priority)} px-2 py-0.5 rounded text-xs font-medium border capitalize`}>
                {followup.priority}
              </span>
              {overdue && (
                <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-medium">
                  Overdue
                </span>
              )}
            </div>
            {followup.description && (
              <p className="text-sm text-slate-500 mt-1">{followup.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 flex-wrap">
              {lead && (
                <Link
                  to={`/leads/${lead.id}`}
                  className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  {lead.name}
                  <ExternalLink className="w-3 h-3" />
                </Link>
              )}
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <CalendarIcon className="w-3 h-3" />
                {format(new Date(followup.scheduled_at), "PPP")}
              </span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(followup.id)}
            className="text-slate-400 hover:text-red-600"
            data-testid={`delete-followup-${followup.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Follow-ups
          </h1>
          <p className="text-slate-500 mt-1">{followups.filter(f => !f.completed).length} pending tasks</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportFollowupsControls />
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white btn-press" data-testid="add-followup-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Follow-up
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Schedule Follow-up</DialogTitle>
              <DialogDescription>Create a reminder to follow up with a lead</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateFollowup} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Lead *</Label>
                <Select
                  value={newFollowup.lead_id}
                  onValueChange={(value) => setNewFollowup({ ...newFollowup, lead_id: value })}
                >
                  <SelectTrigger data-testid="followup-lead-select">
                    <SelectValue placeholder="Select a lead" />
                  </SelectTrigger>
                  <SelectContent>
                    {allLeads.map((lead) => (
                      <SelectItem key={lead.id} value={lead.id}>
                        {lead.name} - {lead.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={newFollowup.title}
                  onChange={(e) => setNewFollowup({ ...newFollowup, title: e.target.value })}
                  placeholder="e.g., Follow up on quote"
                  required
                  data-testid="followup-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={newFollowup.description}
                  onChange={(e) => setNewFollowup({ ...newFollowup, description: e.target.value })}
                  rows={2}
                  data-testid="followup-desc-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Scheduled Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="followup-date-btn">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {format(selectedDate, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => date && setSelectedDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={newFollowup.priority}
                  onValueChange={(value) => setNewFollowup({ ...newFollowup, priority: value })}
                >
                  <SelectTrigger data-testid="followup-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={!newFollowup.lead_id || !newFollowup.title}
                  data-testid="submit-followup-btn"
                >
                  Schedule
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={filters.completed !== "" || filters.priority ? "border-blue-600 text-blue-600" : ""}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 animate-fade-in">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={filters.completed}
                    onValueChange={(value) => setFilters({ ...filters, completed: value })}
                  >
                    <SelectTrigger data-testid="filter-completed-select">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All</SelectItem>
                      <SelectItem value="false">Pending</SelectItem>
                      <SelectItem value="true">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={filters.priority}
                    onValueChange={(value) => setFilters({ ...filters, priority: value })}
                  >
                    <SelectTrigger data-testid="filter-priority-select">
                      <SelectValue placeholder="All priorities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All priorities</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Follow-ups List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Overdue */}
          {groupedFollowups.overdue.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-red-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Overdue ({groupedFollowups.overdue.length})
              </h2>
              <div className="space-y-3">
                {groupedFollowups.overdue.map(renderFollowupCard)}
              </div>
            </div>
          )}

          {/* Today */}
          {groupedFollowups.today.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Today ({groupedFollowups.today.length})
              </h2>
              <div className="space-y-3">
                {groupedFollowups.today.map(renderFollowupCard)}
              </div>
            </div>
          )}

          {/* Upcoming */}
          {groupedFollowups.upcoming.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                Upcoming ({groupedFollowups.upcoming.length})
              </h2>
              <div className="space-y-3">
                {groupedFollowups.upcoming.map(renderFollowupCard)}
              </div>
            </div>
          )}

          {/* Completed */}
          {groupedFollowups.completed.length > 0 && filters.completed !== "false" && (
            <div>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Completed ({groupedFollowups.completed.length})
              </h2>
              <div className="space-y-3">
                {groupedFollowups.completed.map(renderFollowupCard)}
              </div>
            </div>
          )}

          {/* Empty State */}
          {followups.length === 0 && (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No follow-ups scheduled</p>
                <p className="text-sm text-slate-400 mt-1">Create your first follow-up to get started</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default FollowUps;
